export const BRAND_SAFETY_INITIAL_FLAGS = {
  AlcoholFlag: 0,
  SmokingFlag: 0,
  NarcoticsFlag: 0,
  MarijuanaFlag: 0,
  TherapeuticsFlag: 0,
  KnivesFlag: 0,
  GunsFlag: 0,
  HateFlag: 0,
  PoliticsFlag: 0,
  ReligionFlag: 0,
  AdultFlag: 0,
  TrademarkFlag: 0,
};

export const BRAND_SAFETY_LABELS = {
  AlcoholFlag: 'Alcohol',
  SmokingFlag: 'Tobacco / Nicotine',
  NarcoticsFlag: 'Narcotics',
  MarijuanaFlag: 'Marijuana',
  TherapeuticsFlag: 'Therapeutics Drugs',
  KnivesFlag: 'Knives',
  GunsFlag: 'Guns',
  HateFlag: 'Hate Content',
  PoliticsFlag: 'Political Content',
  ReligionFlag: 'Religious Content',
  AdultFlag: 'Adult Content',
  TrademarkFlag: 'Trademark Detection',
  DepictingViolenceFlag: 'Depicting Violence',
  ThirdPartyContentFlag: '3rd Party Content',
};

export const BRAND_SAFETY_CATEGORIES = [
  {
    name: 'Identify Harmful Substance',
    items: ['AlcoholFlag', 'SmokingFlag', 'NarcoticsFlag', 'MarijuanaFlag', 'TherapeuticsFlag'],
  },
  {
    name: 'Potentially Offensive Content',
    items: ['HateFlag', 'ReligionFlag', 'PoliticsFlag', 'AdultFlag'],
  },
  {
    name: 'Identify Harmful Objects',
    items: ['KnivesFlag', 'GunsFlag'],
  },
  {
    name: 'Legal And Ethical Issues',
    items: ['TrademarkFlag'],
  },
];

// ---------------------------------------------------------------------------
// Brand Safety Severity (per-flag severity preferences)
// ---------------------------------------------------------------------------

// The four severity levels every flag can be configured for, ordered from most
// to least severe. `color` drives the leading dot in the UI (risk scale).
export const BRAND_SAFETY_SEVERITY_LEVELS = [
  { key: 'severe', label: 'Severe', color: '#B42318' },
  { key: 'moderate', label: 'Moderate', color: '#B54708' },
  { key: 'low', label: 'Low', color: '#CA8504' },
  { key: 'minor', label: 'Minor', color: '#98A2B3' },
];

// Canonical ordered list of severity keys: ['severe','moderate','low','minor'].
export const BRAND_SAFETY_SEVERITY_KEYS = BRAND_SAFETY_SEVERITY_LEVELS.map((level) => level.key);

// Layout for the severity form, mirroring the Figma design. Sections are arranged
// in a grid of `row` x `column`; each row is a horizontally-aligned band of two
// columns, so the sections in row 1 line up regardless of how tall the row 0
// sections are. Flags are still keyed by the canonical `*Flag` identifiers used
// across the brand safety module.
export const BRAND_SAFETY_SEVERITY_SECTIONS = [
  {
    row: 0,
    column: 0,
    title: 'Harmful Substances',
    items: ['AlcoholFlag', 'NarcoticsFlag', 'SmokingFlag', 'MarijuanaFlag', 'TherapeuticsFlag'],
  },
  {
    row: 0,
    column: 1,
    title: 'Offensive Content',
    items: ['HateFlag', 'PoliticsFlag', 'ReligionFlag', 'AdultFlag'],
  },
  {
    row: 1,
    column: 0,
    title: 'Identify Harmful Objects',
    items: ['KnivesFlag', 'GunsFlag'],
  },
  {
    row: 1,
    column: 1,
    title: 'Legal and Ethical Issues',
    items: ['TrademarkFlag'],
  },
];

// Flat, ordered list of every flag key the severity form manages.
export const BRAND_SAFETY_SEVERITY_FLAG_KEYS = BRAND_SAFETY_SEVERITY_SECTIONS.reduce(
  (keys, section) => keys.concat(section.items),
  []
);

export const BRAND_SAFETY_MEASURE_STATUS = {
  ACCEPTED: 'Reviewed & Accepted',
  FLAGGED: 'Reviewed & Flagged',
  NOT_REVIEWED: 'Not Reviewed',
  IGNORED: 'Ignored Flags',
  REMOVED: 'Removed Flags',
};

// Configuration for the BrandSafety component
export const BRAND_SAFETY_CONFIG = {
  SIZE_PER_ROW: 5,
  ROW_PER_PAGE: 4,
  CONTAINER_HEIGHT: 800,
  SHOW_HEADER: true,
  DEFAULT_LAYOUT: 'LIST',
};

// Helper function to get flag display name
export const getFlagDisplayName = (flagKey) => {
  return BRAND_SAFETY_LABELS[flagKey] || flagKey;
};

// Helper function to get category by flag
export const getCategoryByFlag = (flagKey) => {
  return BRAND_SAFETY_CATEGORIES.find((category) => category.items.includes(flagKey));
};

// Helper function to process active flags and group them by categories
export const processActiveFlags = (activeFlags) => {
  if (!activeFlags || !Array.isArray(activeFlags)) {
    return [];
  }

  const groupedFlags = {};

  // Group flags by category
  activeFlags.forEach((flag) => {
    const category = getCategoryByFlag(flag);
    if (category) {
      if (!groupedFlags[category.name]) {
        groupedFlags[category.name] = [];
      }
      groupedFlags[category.name].push(getFlagDisplayName(flag));
    }
  });

  // Convert to array format for easier rendering
  return Object.entries(groupedFlags).map(([categoryName, flagNames]) => ({
    categoryName,
    flagNames,
  }));
};

export const SHORT_VIDEO_HEIGHT = 300;
export const SHORT_VIDEO_ASPECT_RATIO = 7 / 10;
export const SHORT_VIDEO_WIDTH = SHORT_VIDEO_HEIGHT * SHORT_VIDEO_ASPECT_RATIO;
